import { AppBuilderStreamAPIName } from 'src/constants';
import { buildApiEndpointUrl, getInitForApi } from 'src/utils/AmplifyApiUtils';

import type { MessagePart, SkillsConfig } from './builderSessionApi';

import { ApiMethods, ApiTags, appAPI } from '.';

export type AppBuilderStatus =
  | 'generating'
  | 'ready'
  | 'awaiting_config'
  | 'deploying'
  | 'deployed'
  | 'installed'
  | 'failed';

export interface RequiredSecret {
  key: string;
  label: string;
  description: string;
  type: 'url' | 'api_key' | 'secret' | 'text';
  docsUrl?: string;
}

export interface AppBuilder {
  id: string;
  object: 'appBuilder';
  name: string;
  description: string;
  icon: string;
  status: AppBuilderStatus;
  prompt: string;
  useProductAgent?: boolean;
  v0ProjectId: string;
  v0ChatId: string;
  v0LatestVersionId: string;
  v0DeploymentId: string;
  v0ChatUrl: string;
  v0DemoUrl: string;
  deploymentUrl: string;
  sandboxUrl?: string;
  deployErrors?: string;
  installedAppId: string;
  installedModuleId: string;
  modelConfig?: ModelConfig;
  createdBy: string;
  createdAt: string;
  updatedAt: string;
  requiredSecrets?: RequiredSecret[];
}

export interface SkillInfo {
  name: string;
  description: string;
}

export interface PromptInfo {
  filename: string;
  content: string;
}

export interface PromptSlotVersion {
  id: string;
  title: string;
  version: number;
  isDefault: boolean;
  updatedAt: string;
}

export interface PromptSlot {
  slug: string;
  label: string;
  category: string;
  defaultContent: string;
  versions: PromptSlotVersion[];
}

export interface BuildConfigResponse {
  skills: SkillInfo[];
  prompts: {
    planner: PromptInfo;
    builder: PromptInfo;
  };
  slots?: PromptSlot[];
}

export interface CreateAppBuilderInput {
  name: string;
  description: string;
  prompt: string;
  icon: string;
  instructions?: string;
  branch?: string;
}

export interface UpdateAppBuilderInput {
  id: string;
  name: string;
}

export interface SendAppBuilderMessageInput {
  id: string;
  message: string;
}

export type AppBuilderMessageActionHint =
  | 'resume'
  | 'open_in_v0'
  | 'send_followup'
  | 'skip'
  | 'allow';

/**
 * MessageBinaryFormat is the v0 SDK's structured AST format for rich content.
 * Each entry is a tuple: [type, data] where type 0 = markdown AST, type 1 = metadata.
 */

export type MessageBinaryFormat = [number, ...any[]][];

export interface AppBuilderMessage {
  id: string;
  content: string;
  experimentalContent?: MessageBinaryFormat;
  type: string;
  role: string;
  turnId?: string;
  createdAt?: string;
  updatedAt?: string;
  finishReason?: string;
  actionHints?: AppBuilderMessageActionHint[];
  /** Raw message parts from the builder session API. Populated for task_list messages. */
  parts?: MessagePart[];
}

export interface AppBuilderVersionDiff {
  versionId: string;
  versionNumber: number;
  createdAt: string;
  changedFiles: string[];
  addedFiles?: string[];
  removedFiles?: string[];
}

export interface AppBuilderVersionDiffsResponse {
  data: AppBuilderVersionDiff[];
}

export interface AppBuilderMessagesResponse {
  data: AppBuilderMessage[];
}

interface SendMessageResponse {
  messageId?: string;
}

// Product agent types

export interface ProductAgentWizardContext {
  name: string;
  description: string;
  prompt: string;
  icon: string;
}

export interface ProductAgentMessage {
  role: 'user' | 'assistant';
  content: string;
}

export interface ProductAgentQuestionOption {
  label: string;
  description: string;
}

export interface ProductAgentQuestion {
  question: string;
  header: string;
  options: ProductAgentQuestionOption[];
  multiSelect: boolean;
}

export interface ProductAgentChatInput {
  wizardContext: ProductAgentWizardContext;
  messages: ProductAgentMessage[];
}

export interface ProductAgentResponse {
  message: string;
  questions?: ProductAgentQuestion[];
  isComplete: boolean;
  finalizedPrompt?: string;
}

export class AppBuilderStreamError extends Error {
  status?: number;

  constructor(message: string, status?: number) {
    super(message);
    this.name = 'AppBuilderStreamError';
    this.status = status;
  }
}

const getStreamErrorMessage = async (response: Response) => {
  try {
    const payload = await response.json();
    if (
      payload &&
      typeof payload === 'object' &&
      'message' in payload &&
      typeof payload.message === 'string'
    ) {
      return payload.message as string;
    }
  } catch (error) {
    // Fall through to the generic message when the response is not JSON.
  }

  return 'Unable to stream message to app builder';
};

export const streamAppBuilderMessage = async ({
  id,
  message,
}: SendAppBuilderMessageInput) => {
  const requestInit = await getInitForApi(AppBuilderStreamAPIName, {
    method: 'POST',
    body: JSON.stringify({ message }),
    headers: {
      'Content-Type': 'application/json',
    },
  });

  const response = await fetch(
    buildApiEndpointUrl(
      AppBuilderStreamAPIName,
      `/app-builders/${id}/messages/stream`,
    ),
    requestInit,
  );

  if (!response.ok) {
    const errorMessage = await getStreamErrorMessage(response);
    throw new AppBuilderStreamError(errorMessage, response.status);
  }

  if (!response.body) {
    throw new AppBuilderStreamError(
      'App builder stream did not return a response body',
      response.status,
    );
  }

  return response.body;
};

export const streamResolveTask = async ({
  id,
  action,
}: {
  id: string;
  action: 'allow' | 'skip';
}) => {
  const requestInit = await getInitForApi(AppBuilderStreamAPIName, {
    method: 'POST',
    body: JSON.stringify({ action }),
    headers: {
      'Content-Type': 'application/json',
    },
  });

  const response = await fetch(
    buildApiEndpointUrl(
      AppBuilderStreamAPIName,
      `/app-builders/${id}/tasks/resolve/stream`,
    ),
    requestInit,
  );

  if (!response.ok) {
    const errorMessage = await getStreamErrorMessage(response);
    throw new AppBuilderStreamError(errorMessage, response.status);
  }

  if (!response.body) {
    throw new AppBuilderStreamError(
      'Resolve task stream did not return a response body',
      response.status,
    );
  }

  return response.body;
};

export interface PlanTask {
  id: string;
  /** Human-readable headline shown to non-technical reviewers. */
  title: string;
  /** 1–2 sentence non-technical summary of what this task delivers. */
  userSummary: string;
  /** Technical implementation instructions for the build agent (LLM). */
  description: string;
  stepsToVerify: string[];
}

/**
 * In-flight task snapshot sent by the server while a task is still streaming.
 * Any field may be partial ("Set up the dat") or missing entirely. Once the
 * task's closing brace arrives the server emits a full PlanTask via the
 * __planTask__ sentinel and the partial is replaced.
 */
export interface PartialPlanTask {
  idx: number;
  id?: string;
  title?: string;
  userSummary?: string;
  description?: string;
  stepsToVerify?: string[];
}

export const isPartialPlanTask = (value: unknown): value is PartialPlanTask => {
  if (typeof value !== 'object' || value === null) return false;
  const v = value as Record<string, unknown>;
  return typeof v.idx === 'number';
};

export const isPlanTask = (value: unknown): value is PlanTask => {
  if (typeof value !== 'object' || value === null) return false;
  const v = value as Record<string, unknown>;
  return (
    typeof v.id === 'string' &&
    typeof v.title === 'string' &&
    typeof v.userSummary === 'string' &&
    typeof v.description === 'string' &&
    Array.isArray(v.stepsToVerify) &&
    v.stepsToVerify.every((s) => typeof s === 'string')
  );
};

export const isPlanTaskArray = (value: unknown): value is PlanTask[] =>
  Array.isArray(value) && value.every(isPlanTask);

export interface BuildEvent {
  type:
    | 'task_started'
    | 'task_log'
    | 'task_completed'
    | 'task_failed'
    | 'deploying'
    | 'completed'
    | 'error';
  taskId?: string;
  line?: string;
  url?: string;
  message?: string;
  token?: string;
}

export interface ModelConfig {
  planningModel?: string;
  planEffort?: string;
  buildModel?: string;
  buildEffort?: string;
}

export interface PrepareBuildOutput {
  appBuilderId: string;
  sandboxName: string;
  repoFullName: string;
  marketplaceAppId: string;
}

export interface StartBuildOutput {
  sessionId: string;
  activeTaskListMessageId: string;
}

export async function prepareBuild(
  appName?: string,
  modelConfig?: ModelConfig,
  prompt?: string,
  useProductAgent?: boolean,
): Promise<PrepareBuildOutput> {
  const requestInit = await getInitForApi(AppBuilderStreamAPIName, {
    method: 'POST',
    body: JSON.stringify({ appName, modelConfig, prompt, useProductAgent }),
    headers: { 'Content-Type': 'application/json' },
  });

  const response = await fetch(
    buildApiEndpointUrl(AppBuilderStreamAPIName, '/app-builders/prepare-build'),
    requestInit,
  );

  if (!response.ok) {
    const errorMessage = await getStreamErrorMessage(response);
    throw new AppBuilderStreamError(errorMessage, response.status);
  }

  return response.json() as Promise<PrepareBuildOutput>;
}

export async function startBuild(
  appBuilderId: string,
  sessionId: string,
  skillsConfig?: SkillsConfig,
): Promise<StartBuildOutput> {
  const requestInit = await getInitForApi(AppBuilderStreamAPIName, {
    method: 'POST',
    body: JSON.stringify({
      sessionId,
      ...(skillsConfig ? { skillsConfig } : {}),
    }),
    headers: { 'Content-Type': 'application/json' },
  });

  const response = await fetch(
    buildApiEndpointUrl(
      AppBuilderStreamAPIName,
      `/app-builders/${appBuilderId}/build`,
    ),
    requestInit,
  );

  if (!response.ok) {
    const errorMessage = await getStreamErrorMessage(response);
    throw new AppBuilderStreamError(errorMessage, response.status);
  }

  return response.json() as Promise<StartBuildOutput>;
}

export const getAppBuilderEntityTag = (id: string) => ({
  type: ApiTags.appBuilders as const,
  id,
});

export const getAppBuilderListTag = () => ({
  type: ApiTags.appBuilders as const,
  id: 'LIST',
});

export const getAppBuilderMessagesTag = (id: string) => ({
  type: ApiTags.appBuilders as const,
  id: `MESSAGES-${id}`,
});

export const appBuilderApi = appAPI.injectEndpoints({
  endpoints: (build) => ({
    createAppBuilder: build.mutation<AppBuilder, CreateAppBuilderInput>({
      query: (body) => ({
        path: '/app-builders',
        method: ApiMethods.post,
        options: { body },
      }),
      invalidatesTags: [getAppBuilderListTag()],
    }),
    listAppBuilders: build.query<AppBuilder[], void>({
      query: () => ({
        path: '/app-builders',
        method: ApiMethods.get,
        options: {},
      }),
      providesTags: (result) => {
        const itemTags =
          result?.map((item) => getAppBuilderEntityTag(item.id)) ?? [];
        return [getAppBuilderListTag(), ...itemTags];
      },
    }),
    getAppBuilder: build.query<AppBuilder, { id: string }>({
      query: ({ id }) => ({
        path: `/app-builders/${id}`,
        method: ApiMethods.get,
        options: {},
      }),
      providesTags: (_, __, arg) => [getAppBuilderEntityTag(arg.id)],
    }),
    sendAppBuilderMessage: build.mutation<
      SendMessageResponse,
      SendAppBuilderMessageInput
    >({
      query: ({ id, message }) => ({
        path: `/app-builders/${id}/messages`,
        method: ApiMethods.post,
        options: { body: { message } },
      }),
      invalidatesTags: (_, __, arg) => [getAppBuilderEntityTag(arg.id)],
    }),
    listAppBuilderVersions: build.query<
      AppBuilderVersionDiffsResponse,
      { id: string }
    >({
      query: ({ id }) => ({
        path: `/app-builders/${id}/versions`,
        method: ApiMethods.get,
        options: {},
      }),
      providesTags: (_, __, arg) => [getAppBuilderEntityTag(arg.id)],
    }),
    listAppBuilderMessages: build.query<
      AppBuilderMessagesResponse,
      {
        id: string;
        limit?: number;
        cursor?: string;
      }
    >({
      query: ({ id, limit, cursor }) => ({
        path: `/app-builders/${id}/messages`,
        method: ApiMethods.get,
        options: {
          queryStringParameters: {
            ...(limit ? { limit: String(limit) } : {}),
            ...(cursor ? { cursor } : {}),
          },
        },
      }),
      providesTags: (_, __, arg) => [getAppBuilderMessagesTag(arg.id)],
    }),
    resumeAppBuilderMessage: build.mutation<
      AppBuilderMessage,
      { id: string; messageId: string }
    >({
      query: ({ id, messageId }) => ({
        path: `/app-builders/${id}/messages/${messageId}/resume`,
        method: ApiMethods.post,
        options: {},
      }),
      invalidatesTags: (_, __, arg) => [getAppBuilderEntityTag(arg.id)],
    }),
    resolveAppBuilderTask: build.mutation<
      { success: boolean },
      { id: string; action: 'allow' | 'skip' }
    >({
      query: ({ id, action }) => ({
        path: `/app-builders/${id}/tasks/resolve`,
        method: ApiMethods.post,
        options: { body: { action } },
      }),
      invalidatesTags: (_, __, arg) => [getAppBuilderEntityTag(arg.id)],
    }),
    deployAppBuilder: build.mutation<AppBuilder, { id: string }>({
      query: ({ id }) => ({
        path: `/app-builders/${id}/deploy`,
        method: ApiMethods.post,
        options: {},
      }),
      invalidatesTags: (_, __, arg) => [
        getAppBuilderEntityTag(arg.id),
        getAppBuilderListTag(),
      ],
    }),
    submitAppBuilderSecrets: build.mutation<
      AppBuilder,
      { id: string; values: Record<string, string> }
    >({
      query: ({ id, values }) => ({
        path: `/app-builders/${id}/secrets`,
        method: ApiMethods.post,
        options: { body: { values } },
      }),
      invalidatesTags: (_, __, arg) => [
        getAppBuilderEntityTag(arg.id),
        getAppBuilderListTag(),
      ],
    }),
    updateAppBuilder: build.mutation<AppBuilder, UpdateAppBuilderInput>({
      query: ({ id, ...body }) => ({
        path: `/app-builders/${id}`,
        method: ApiMethods.patch,
        options: { body },
      }),
      invalidatesTags: (_, __, arg) => [getAppBuilderEntityTag(arg.id)],
    }),
    deleteAppBuilder: build.mutation<{ success: boolean }, { id: string }>({
      query: ({ id }) => ({
        path: `/app-builders/${id}`,
        method: ApiMethods.del,
        options: {},
      }),
      invalidatesTags: [ApiTags.appBuilders],
    }),
    getBuildConfig: build.query<BuildConfigResponse, void>({
      query: () => ({
        path: '/app-builders/build-config',
        method: ApiMethods.get,
        options: {},
      }),
      providesTags: [ApiTags.contextItems],
    }),
  }),
});

export const {
  useCreateAppBuilderMutation,
  useListAppBuildersQuery,
  useListAppBuilderVersionsQuery,
  useGetAppBuilderQuery,
  useSendAppBuilderMessageMutation,
  useListAppBuilderMessagesQuery,
  useResumeAppBuilderMessageMutation,
  useResolveAppBuilderTaskMutation,
  useDeployAppBuilderMutation,
  useSubmitAppBuilderSecretsMutation,
  useUpdateAppBuilderMutation,
  useDeleteAppBuilderMutation,
  useGetBuildConfigQuery,
} = appBuilderApi;
